// NEW_FILE_JUSTIFIED: New component in voiceAgents feature directory; unique to this feature
import { useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Pencil, Trash2, Phone } from 'lucide-react';
import type { IVoiceAgentCardProps } from '@/@types/voiceAgents';

export function VoiceAgentCard({
  agent,
  phoneNumber,
  onEdit,
  onToggleActive,
  onDelete,
}: IVoiceAgentCardProps) {
  const handleToggle = useCallback(
    (checked: boolean) => onToggleActive(agent._id, checked),
    [agent._id, onToggleActive]
  );

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base truncate">{agent.name}</CardTitle>
          <Badge variant={agent.isActive ? 'default' : 'secondary'}>
            {agent.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <CardDescription className="line-clamp-2 text-xs">{agent.systemPrompt}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Type</span>
          <Badge variant="outline" className="capitalize">
            {agent.type}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">LLM</span>
          <span className="text-xs font-mono truncate max-w-32">{agent.llmModel}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Tools</span>
          <span className="text-xs">{agent.allowedTools.length} configured</span>
        </div>
        {phoneNumber && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Phone</span>
            <span className="text-xs font-mono flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {phoneNumber.phoneNumber}
            </span>
          </div>
        )}
      </CardContent>

      <CardFooter className="mt-auto pt-3 border-t flex items-center justify-between">
        <Switch checked={agent.isActive} onCheckedChange={handleToggle} />
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(agent._id)} title="Edit agent">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(agent._id)}
            title="Delete agent"
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
